// Pure helpers for the flyover-video share flow. Kept out of the Vue component so the reuse decision
// and the social-share URL builders are unit-testable. Web reality: unlike the mobile apps, a browser
// can't push an MP4 into a specific app, so social targets share the recording LINK (rich OG preview),
// "Save video" downloads the MP4, and "Copy link" copies the video URL.

export interface FlyoverVideoLite {
  id?: string;
  status: string;
  url?: string | null;
  aspect?: string | null;
  mapStyleUrl?: string | null;
  renderedAt?: string | null;
  // PRO+ video options this render was made with. Nullable on the wire (a row cached by an older
  // build has no value), and null reads as false — the branded default every pre-existing row is.
  hideLogo?: boolean | null;
  hideStats?: boolean | null;
  skipIntro?: boolean | null;
}

// The style the backend renders with when a client sends none. Kept in sync with
// FlyoverVideo::DEFAULT_MAP_STYLE_URL so the reuse check below lines up with what the server cached.
export const DEFAULT_MAP_STYLE_URL = 'mapbox://styles/mapbox/satellite-streets-v12';

/**
 * Canonicalise a style URL so it's a stable cache key. HiiKER style-JSON URLs carry a per-user
 * `?userId=…` query param (view-tracking only — it doesn't change the returned style), so two users
 * viewing the same style would otherwise never match. Strip it (and any empty string) down to the
 * default so generation and reuse compare like-for-like. Non-userId params are preserved.
 */
export function normalizeStyleUrl(url?: string | null): string {
  const raw = (url ?? '').trim();
  if (!raw) return DEFAULT_MAP_STYLE_URL;
  const [base, query] = raw.split('?');
  if (!query) return raw;
  const kept = query
    .split('&')
    .filter((p) => p && !p.toLowerCase().startsWith('userid='));
  return kept.length ? `${base}?${kept.join('&')}` : base;
}

/**
 * The flyover video to reuse for the requested style and options, or null. A recording caches one
 * render per (style, options) — see FlyoverVideo's uniqueness scope — so only a READY video matching
 * BOTH qualifies; otherwise we must fall through to the generate flow. The backend deletes a
 * recording's videos when its route actually changes, so no freshness check is needed. Newest
 * matching render wins.
 *
 * The options half matters because reusing SKIPS the format step, which is where they are chosen: a
 * member whose last export was bare-bones would otherwise be handed the branded render with no
 * indication their preference had been dropped, and no way to ask for another.
 */
export function pickReusableVideo(
  videos: FlyoverVideoLite[],
  styleUrl?: string | null,
  options: ReusableVideoOptions = DEFAULT_REUSE_OPTIONS,
): FlyoverVideoLite | null {
  const wanted = normalizeStyleUrl(styleUrl);
  const ready = videos.filter(
    (v) => v.status === 'READY'
      && !!v.url
      && normalizeStyleUrl(v.mapStyleUrl) === wanted
      && matchesOptions(v, options),
  );
  if (ready.length === 0) return null;
  return ready.reduce((best, v) => {
    const bestAt = Date.parse(best.renderedAt ?? '') || 0;
    const vAt = Date.parse(v.renderedAt ?? '') || 0;
    return vAt > bestAt ? v : best;
  });
}

/** The option set a reuse candidate has to have been rendered with. */
export interface ReusableVideoOptions {
  hideLogo: boolean;
  hideStats: boolean;
  skipIntro: boolean;
}

const DEFAULT_REUSE_OPTIONS: ReusableVideoOptions = { hideLogo: false, hideStats: false, skipIntro: false };

/** Null/undefined on a row means the branded default, which is what every pre-existing render is. */
function matchesOptions(video: FlyoverVideoLite, options: ReusableVideoOptions): boolean {
  const on = (value: boolean | null | undefined) => value === true;
  return on(video.hideLogo) === options.hideLogo
    && on(video.hideStats) === options.hideStats
    && on(video.skipIntro) === options.skipIntro;
}

/** Portrait (9:16) unless the aspect is explicitly landscape. */
export function isVerticalAspect(aspect?: string | null): boolean {
  return aspect !== 'LANDSCAPE_16_9' && aspect !== '16x9';
}

// GraphQL aspect enum values sent to the backend.
export const ASPECT_VERTICAL = 'PORTRAIT_9_16';
export const ASPECT_HORIZONTAL = 'LANDSCAPE_16_9';

export function facebookShareUrl(url: string): string {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
}

export function whatsappShareUrl(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

export function emailShareUrl(subject: string, body: string): string {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/** A filename for the downloaded MP4, e.g. "my-hike-flyover.mp4". */
export function videoFileName(title: string): string {
  const slug = (title || 'hike')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 60);
  return `${slug || 'hike'}-flyover.mp4`;
}
